// ───────────────────────────────────────────────────────────────────────────
// Butterfly Camping Resort — shared hi-fi kit
// Exposes: Photo, Btn, Stat, Eyebrow, Reveal, LeafMark, Nav, Footer, InteriorHero
// Nav routing handled by global `go(page)` + `useRoute()` provided in bc-app.jsx
// ───────────────────────────────────────────────────────────────────────────
const { useState, useEffect, useRef } = React;

// Real-path nav: links render a genuine href (crawlable + ⌘/ctrl-click opens a
// new tab) while a plain left-click routes in-app. idToPath lives in bc-store.jsx.
const idToPath = window.BCStore.idToPath;
const navClick = (id, go) => (e) => {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  go(id);
};

// Small inline icons --------------------------------------------------------
const Ico = {
  phone: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>),
  arrow: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>),
  check: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="20 6 9 17 4 12"/></svg>),
  pin: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>),
  star: (p) => (<svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12 2l2.9 6.2 6.8.9-5 4.7 1.3 6.8L12 18.6 5.9 21.3 7.2 14.5l-5-4.7 6.8-.9z"/></svg>),
  cal: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>),
  chev: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="6 9 12 15 18 9"/></svg>),
  gift: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/></svg>),
  alert: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>),
  info: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>),
  // Lodging-type glyphs — shared by the "Stay" nav dropdown and the home cards.
  rv: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M3 6h10l5 4.5V16"/><path d="M3 16V6"/><path d="M3 16h2.5"/><path d="M10.5 16h4.5"/><path d="M20 16h1"/><path d="M13 7.5v3.5h4.5"/><circle cx="8" cy="16" r="2"/><circle cx="17.5" cy="16" r="2"/></svg>),
  tent: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 4 3 20h18L12 4z"/><path d="M12 4v16"/><path d="m12 12-4 8"/><path d="m12 12 4 8"/></svg>),
  cabin: (p) => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M3 11 12 4l9 7"/><path d="M5 9.5V20h14V9.5"/><path d="M10 20v-5h4v5"/></svg>),
};

// Leaf / butterfly mark -----------------------------------------------------
const LeafMark = ({ size = 22, color = "#2f5e2a" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M12 2C8 6 4 8.5 4 13.5c0 4 3.4 7.2 8 8.5 4.6-1.3 8-4.5 8-8.5C20 8.5 16 6 12 2z" fill={color}/>
    <path d="M12 6c-2.2 3-3 5.6-1.6 8.4C11.8 16.6 12 18 12 18s.2-1.4 1.6-3.6C15 11.6 14.2 9 12 6z" fill="#fff" opacity=".26"/>
    <path d="M12 9v9" stroke="#fff" strokeOpacity=".3" strokeWidth="1"/>
  </svg>
);

// Responsive images ---------------------------------------------------------
// The site used to hand every device the full-resolution upload — a 1200x1600
// photo painted into a 364px card. `npm run responsive-images` pre-renders a
// width ladder into /rs/ and emits window.BCIMG (filename → [w, h, [widths]]).
//
// Pic() emits srcset/sizes off that map plus intrinsic width/height (which is
// what actually kills layout shift). An upload with no manifest entry — the
// client just added it through the admin, variants not regenerated yet —
// returns a plain <img src> and renders exactly as it always has.
const UP = "/uploads/";
const imgMeta = (src) => {
  if (typeof src !== "string" || src.lastIndexOf(UP, 0) !== 0) return null;
  const m = window.BCIMG && window.BCIMG[src.slice(UP.length)];
  return m && m[2] && m[2].length ? { w: m[0], h: m[1], widths: m[2] } : null;
};
const srcsetFor = (src, meta) => {
  const base = src.slice(UP.length).replace(/\.[^.]+$/, "");
  return meta.widths.map((w) => `/rs/${base}-${w}.webp ${w}w`).join(", ");
};

// `sizes` must describe the slot the image paints into, or the browser picks
// the wrong rung. Defaults to 100vw (never under-fetches); call sites that know
// their slot pass something tighter.
const Pic = ({ src, sizes = "100vw", className, alt = "", eager, style, onClick }) => {
  const meta = imgMeta(src);
  const img = (
    <img className={className} src={src} alt={alt} decoding="async" style={style} onClick={onClick}
      loading={eager ? "eager" : "lazy"} fetchpriority={eager ? "high" : undefined}
      width={meta ? meta.w : undefined} height={meta ? meta.h : undefined}
      sizes={meta ? sizes : undefined} srcSet={meta ? srcsetFor(src, meta) : undefined} />
  );
  return img;
};

// Photo placeholder ---------------------------------------------------------
// Renders a real <img> (not a CSS background) so the browser can lazy-load
// offscreen photos natively; pass `eager` for above-the-fold/LCP images (the
// home hero) to fetch them at high priority instead.
const Photo = ({ tone = "pine", label, h, r = 0, className = "", style = {}, children, align = "flex-end", src, eager, sizes }) => (
  <div className={`photo t-${tone} ${className}`}
    style={{ height: h, borderRadius: r, alignItems: align, ...style }}>
    {src && <Pic className="photo__img" src={src} eager={eager} sizes={sizes} />}
    {!src && <div className="photo__grain" />}
    {label && <span className="photo__tag">{label}</span>}
    {children}
  </div>
);

// Button --------------------------------------------------------------------
// Renders a real <a> when `href` is passed (so tel:/mailto:/link CTAs work like
// the live site), otherwise a <button>. Same classes + slots either way.
const Btn = ({ kind = "primary", size, block, children, icon, iconRight, onClick, style, href, target }) => {
  const cls = `btn btn--${kind}${size ? " btn--" + size : ""}${block ? " btn--block" : ""}`;
  const inner = <>{icon}{children}{iconRight}</>;
  return href
    ? <a className={cls} href={href} target={target} onClick={onClick} style={{ textDecoration: "none", ...style }}>{inner}</a>
    : <button className={cls} onClick={onClick} style={style}>{inner}</button>;
};

const Eyebrow = ({ children, style }) => <div className="eyebrow" style={style}>{children}</div>;

const Stat = ({ n, l, color = "var(--green)", size = 40, lcolor }) => (
  <div>
    <div className="statn" style={{ fontSize: size, color }}>{n}</div>
    <div className="statl" style={{ color: lcolor }}>{l}</div>
  </div>
);

// Scroll-reveal wrapper -----------------------------------------------------
// Reveals the element when it scrolls into view (IntersectionObserver), then
// stops observing so it stays put. `delay` (ms) staggers siblings via
// transition-delay. Safety: if IO is unavailable we reveal immediately, and
// reduced-motion shows everything with no transition (see .reveal CSS).
// `now` — render already revealed, with no IntersectionObserver and no
// transition. Used for above-the-fold blocks the server already painted (and
// faded in) via .reveal--auto: re-running the animation here would show the
// visitor a second fade when React replaces the server markup.
const Reveal = ({ children, delay = 0, as = "div", className = "", style = {}, now }) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(!!now);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen || now) return;
    if (typeof IntersectionObserver === "undefined") { setSeen(true); return; }
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((e) => { if (e.isIntersecting) { setSeen(true); obs.disconnect(); } });
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const Tag = as;
  return (
    <Tag ref={ref} className={`reveal ${seen ? "reveal--in" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms`, ...style }}>
      {children}
    </Tag>
  );
};

// ── NAV ─────────────────────────────────────────────────────────────────────
const STAY_ITEMS = [
  ["rv", "RV Sites", "128 full-hookup sites", "pine"],
  ["tent", "Tent Sites", "6 quiet wooded sites", "meadow"],
  ["cabins", "Cabins", "8 cozy cabins, sleeps 4–6", "dusk"],
];

// One distinct glyph per lodging type — used in BOTH the nav dropdown and the
// home "Pick your spot" cards so each stay reads as itself in both places.
const STAY_ICONS = { rv: Ico.rv, tent: Ico.tent, cabins: Ico.cabin };

const Nav = ({ route, go, onHero, openDrawer, annH = 0, navRef }) => {
  const [data] = window.BCStore.useStore();
  const nav = data.nav, phone = data.contact.phone;
  const brand = data.brand || {};
  const stayItems = STAY_ITEMS.filter(([id]) => nav[id] !== false);
  const topLinks = [["activities","Activities"],["events","Events"],["gallery","Gallery"],["blog","Blog"],["rules","Rules"]]
    .filter(([id]) => nav[id] !== false);

  const [solid, setSolid] = useState(!onHero);
  useEffect(() => {
    if (!onHero) { setSolid(true); return; }
    const on = () => setSolid(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [onHero]);

  const light = onHero && !solid;
  const markBg = light ? "rgba(255,255,255,.16)" : "var(--green)";

  return (
    <header ref={navRef} className={`nav ${onHero ? "nav--onhero" : ""} ${light ? "nav--light" : ""}`}
      style={{ top: annH, ...(solid ? { position: onHero ? "fixed" : "sticky", background: "rgba(253,251,246,.9)", backdropFilter: "blur(12px)", borderBottom: "1px solid var(--rule)" } : {}) }}>
      <div className="nav__inner">
        <a className="logo" href={idToPath("home")} onClick={navClick("home", go)}>
          <div className="logo__mark" style={{ background: brand.logo ? "transparent" : markBg, overflow: "hidden" }}>
            {brand.logo
              ? <img src={brand.logo} alt={brand.name || "logo"} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              : <LeafMark size={46} color="#fff" />}
          </div>
          <div>
            <div className="logo__name">{brand.name}</div>
            <div className="logo__sub">{brand.sub}</div>
          </div>
        </a>

        <nav className="nav__links">
          {stayItems.length > 0 && (
            <div className="dd">
              <span className={`navlink ${["rv","tent","cabins"].includes(route) ? "navlink--on" : ""}`} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                Stay <Ico.chev style={{ width: 15, height: 15 }} />
              </span>
              <div className="dd__menu">
                {stayItems.map(([id, t, d, tone]) => {
                  const G = STAY_ICONS[id];
                  return (
                  <a className="dd__item" key={id} href={idToPath(id)} onClick={navClick(id, go)}>
                    <div className={`dd__ico t-${tone}`} style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                      {G && <G style={{ width: 20, height: 20 }} />}
                    </div>
                    <div><div className="dd__t">{t}</div><div className="dd__d">{d}</div></div>
                  </a>
                  );
                })}
              </div>
            </div>
          )}
          {topLinks.map(([id,t]) => (
            <a key={id} href={idToPath(id)} className={`navlink ${route===id?"navlink--on":""}`} onClick={navClick(id, go)}>{t}</a>
          ))}
        </nav>

        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <a href={`tel:${window.BCStore.telHref(phone)}`} className="btn btn--amber btn--sm" style={{ textDecoration: "none" }} aria-label={`Call ${phone} to book`}>
            <Ico.phone style={{ width: 15, height: 15 }} /><span className="nav__phone-label">{phone}</span>
          </a>
          <button type="button" className="burger" onClick={openDrawer} aria-label="Open menu"><span/><span/><span/></button>
        </div>
      </div>
    </header>
  );
};

// ── FOOTER ───────────────────────────────────────────────────────────────────
const Footer = ({ go }) => {
  const [data] = window.BCStore.useStore();
  const { contact, season, officeHours, nav } = data;
  const brand = data.brand || {};
  const footer = data.footer || {};
  const exploreLinks = [["rv","RV Sites"],["tent","Tent Sites"],["cabins","Cabins"],["activities","Activities"],["events","Events"],["rules","Rules & Regulations"]]
    .filter(([id]) => nav[id] !== false);
  return (
  <footer className="footer">
    <div className="wrap">
      <div className="footer__cols">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 11, marginBottom: 14 }}>
            <div className="logo__mark" style={{ background: brand.logo ? "transparent" : "rgba(255,255,255,.14)", overflow: "hidden" }}>
              {brand.logo
                ? <img src={brand.logo} alt={brand.name || "logo"} loading="lazy" decoding="async" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                : <LeafMark size={22} color="#fff" />}
            </div>
            <div className="serif" style={{ fontSize: 20, fontWeight: 700, color: "#fff" }}>Butterfly Camping Resort</div>
          </div>
          <p style={{ fontSize: 14, color: "rgba(255,255,255,.62)", lineHeight: 1.7, maxWidth: 300 }}>
            {footer.tagline}
          </p>
          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            {season.chip && (
              <span className="chip chip--static" style={{ borderColor: "rgba(255,255,255,.22)", color: "rgba(255,255,255,.8)", fontSize: 12 }}>{season.chip}</span>
            )}
          </div>
        </div>
        <div>
          <div className="footer__h">Explore</div>
          {exploreLinks.map(([id,t]) => (
            <a key={id} href={idToPath(id)} className="footer__li" onClick={navClick(id, go)}>{t}</a>
          ))}
        </div>
        <div>
          <div className="footer__h">Office Hours</div>
          {officeHours.map(([d, h], i) => (
            <div key={i} className="footer__li" style={{ cursor: "default" }}>{d}&nbsp;&nbsp;{h}</div>
          ))}
        </div>
        <div>
          <div className="footer__h">Contact</div>
          <a href={`tel:${window.BCStore.telHref(contact.phone)}`} className="footer__li">{contact.phone}</a>
          <a href={`mailto:${contact.email}`} className="footer__li">{contact.email}</a>
          {contact.addr1 && <div className="footer__li" style={{ cursor: "default", marginTop: 6 }}>{contact.addr1}</div>}
          {contact.addr2 && <div className="footer__li" style={{ cursor: "default" }}>{contact.addr2}</div>}
        </div>
      </div>
      <div style={{ paddingTop: 22, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontSize: 12.5, color: "rgba(255,255,255,.4)" }}>© 2026 Butterfly Camping Resort. All rights reserved.</span>
        <span style={{ fontSize: 12.5, color: "rgba(255,255,255,.4)" }}>Booking confirmed by phone — we still answer it ourselves.</span>
      </div>
      <div style={{ textAlign: "center", paddingTop: 14, fontSize: 12.5, color: "rgba(255,255,255,.4)" }}>
        Designed, hosted, and maintained by{" "}
        <a href="https://josephwebsitesolutions.com" target="_blank" rel="noopener noreferrer" className="credit-link">Joseph Website Solutions</a>.
      </div>
    </div>
  </footer>
  );
};

// ── INTERIOR HERO ────────────────────────────────────────────────────────────
const InteriorHero = ({ eyebrow, title, sub, stats, photo }) => (
  <section className="ihero">
    {photo && <div className="ihero__bg" style={{ backgroundImage: `linear-gradient(rgba(15,30,18,.62),rgba(15,30,18,.8)), url("${photo}")` }} />}
    <div className="ihero__glow" /><div className="ihero__glow2" />
    <div className="wrap" style={{ position: "relative" }}>
      <Reveal>
        {eyebrow && <Eyebrow style={{ color: "rgba(255,255,255,.5)", marginBottom: 16 }}>{eyebrow}</Eyebrow>}
        <h1 className="display" style={{ fontSize: "clamp(40px,6vw,62px)", color: "#fff", maxWidth: 760 }}>{title}</h1>
        {sub && <p style={{ fontSize: 18, color: "rgba(255,255,255,.74)", lineHeight: 1.6, maxWidth: 560, marginTop: 18 }}>{sub}</p>}
      </Reveal>
      {stats && (
        <Reveal delay={120}>
          <div style={{ display: "flex", gap: 44, marginTop: 34, flexWrap: "wrap" }}>
            {stats.map(([n, l]) => (
              <Stat key={l} n={n} l={l} color="var(--amber)" lcolor="rgba(255,255,255,.5)" size={36} />
            ))}
          </div>
        </Reveal>
      )}
    </div>
  </section>
);

window.BCKit = { Ico, LeafMark, Photo, Pic, Btn, Eyebrow, Stat, Reveal, Nav, Footer, InteriorHero, STAY_ITEMS, STAY_ICONS };
